import { useTranslation } from "react-i18next";

import type { ReactNode } from "react";
import type { StorylineSourceRow } from "@/features/studies/report/storyline";

interface StorylineSourcesProps {
  rows: Array<StorylineSourceRow>;
  findingIndex: number;
  /** The text rows of cuts the finding plots: their accessible read. */
  cutRows?: ReactNode;
}

/** Number-badge visual, neutral in the storyline (`bg-muted`). */
const SOURCE_NUMBER_BADGE_CLASS =
  "inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none bg-muted text-foreground";

/**
 * Where a finding's figures come from: each cited measure, theme or group
 * with its stored figure (a theme's figure says what it counts: "of
 * answers", "of people"; a quotes theme has none) and the twins it rests on,
 * in the analyst tab's evidence disclosure. A plotted cut's text rows read
 * here too.
 */
export function StorylineSources(props: StorylineSourcesProps) {
  const { rows, findingIndex, cutRows } = props;

  const { t } = useTranslation("app");

  if (rows.length === 0 && !cutRows) {
    return null;
  }

  function figureText(row: StorylineSourceRow): string | null {
    if (!row.value || row.unit === "quotes") {
      return null;
    }
    if (row.tableCellContext) {
      const context = row.tableCellContext;
      const scale =
        context.valueUnit === "scale_points" &&
        context.scaleMin !== null &&
        context.scaleMax !== null
          ? ` (${context.scaleMin}–${context.scaleMax})`
          : "";
      const unit =
        context.valueUnit === "scale_points"
          ? ` ${t("studyReport.storyline.figure.unit.points")}`
          : context.valueUnit === "index"
            ? " NPS"
            : "";
      return `${row.value}${unit}${scale}`;
    }
    switch (row.unit) {
      case "answers":
        return `${row.value} ${t("studyReport.storyline.figure.unit.answers")}`;
      case "people":
        return `${row.value} ${t("studyReport.storyline.figure.unit.people")}`;
      default:
        return row.value;
    }
  }

  return (
    <details className="rounded-lg border border-border px-3 py-2">
      <summary
        className="cursor-pointer text-xs font-medium text-muted-foreground"
        data-testid={`storyline-sources-toggle-${findingIndex}`}
      >
        {t("studyReport.storyline.sourcesToggle")}
      </summary>
      <div className="mt-3 flex flex-col gap-2">
        {cutRows}
        {rows.map((row, index) => {
          const figure = figureText(row);
          const testId = `storyline-source-row-${findingIndex}-${index}`;
          return (
            <div
              key={row.key}
              className="flex items-baseline gap-2 text-sm"
              data-testid={testId}
            >
              <span className={SOURCE_NUMBER_BADGE_CLASS}>{index + 1}</span>
              <span className="min-w-0 flex-1 text-foreground">
                {row.label}
                {row.tableCellContext ? (
                  <span className="block text-xs text-muted-foreground">
                    {row.tableCellContext.groupDefinition}
                  </span>
                ) : null}
              </span>
              {figure ? (
                <span
                  className="text-sm font-medium tabular-nums text-foreground"
                  data-testid={`${testId}-value`}
                >
                  {figure}
                </span>
              ) : null}
              {typeof row.base === "number" ? (
                <span className="text-xs tabular-nums text-muted-foreground">
                  {t("studyReport.storyline.base", { count: row.base })}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
    </details>
  );
}
